import React, { useEffect, useMemo, useState } from "react";
import { Plus, Save, Send, X } from "lucide-react";

import api from "../api";
import { useMasterData } from "../../utils/useMasterData";

const emptyItem = (itemType = "GOLD_JEWELLERY") => ({
  description: "",
  item_type: itemType,
  hsn_code: "",
  quantity: 1,
  unit: itemType === "MAKING_CHARGES" ? "service" : "pcs",
  rate: 0,
  weight_g: "",
  purity_pct: "",
  design_code: "",
});

const initialForm = {
  invoice_type: "GST",
  invoice_date: new Date().toISOString().slice(0, 10),
  due_date: "",
  customer_id: "",
  discount_pct: 0,
  advance_deducted: 0,
  other_charges: 0,
  payment_terms: "",
  notes: "",
  items: [emptyItem()],
};

function buildInitialForm(initialInvoiceType, initialData = {}) {
  const seededItems = Array.isArray(initialData.items) && initialData.items.length
    ? initialData.items.map((item) => ({
        ...emptyItem(item.item_type || "GOLD_JEWELLERY"),
        ...item,
      }))
    : initialForm.items;

  return {
    ...initialForm,
    ...initialData,
    invoice_type: initialData.invoice_type || initialInvoiceType,
    items: seededItems,
  };
}

function numberValue(value) {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function findRate(hsnCodes, itemType, supplyType, isGstInvoice) {
  const code = hsnCodes.find((row) => row.product_category === itemType);
  if (!code || !isGstInvoice || code.is_exempt) {
    return { hsn_code: code?.hsn_code || "", cgst: 0, sgst: 0, igst: 0 };
  }
  if (supplyType === "INTER") {
    return { hsn_code: code.hsn_code, cgst: 0, sgst: 0, igst: numberValue(code.igst_rate) };
  }
  return {
    hsn_code: code.hsn_code,
    cgst: numberValue(code.cgst_rate),
    sgst: numberValue(code.sgst_rate),
    igst: 0,
  };
}

function InvoiceCreate({ onCancel, onCreated, initialInvoiceType = "GST", initialData = {} }) {
  const { getOptions, getValues } = useMasterData(["PRODUCT_CATEGORY_GST", "INVOICE_TYPE"]);
  const itemTypes = getValues("PRODUCT_CATEGORY_GST");
  const invoiceTypes = getOptions("INVOICE_TYPE");
  const [form, setForm] = useState(() => buildInitialForm(initialInvoiceType, initialData));
  const [meta, setMeta] = useState({ next_invoice_no: "", gst_config: null, customers: [] });
  const [hsnCodes, setHsnCodes] = useState([]);
  const [customerQuery, setCustomerQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadData(initialInvoiceType);
  }, [initialInvoiceType]);

  useEffect(() => {
    setForm(buildInitialForm(initialInvoiceType, initialData));
    setCustomerQuery("");
  }, [initialInvoiceType, initialData]);

  const loadData = async (invoiceType) => {
    setLoading(true);
    try {
      const [metaRes, hsnRes] = await Promise.all([
        api.get(`/accounts/invoices/meta?invoice_type=${invoiceType}`),
        api.get("/accounts/gst/hsn-codes"),
      ]);
      setMeta(metaRes.data);
      setHsnCodes(hsnRes.data.data || []);
      setForm((prev) => ({ ...prev, invoice_type: invoiceType }));
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load invoice setup.");
    } finally {
      setLoading(false);
    }
  };

  const selectedCustomer = useMemo(
    () => meta.customers.find((customer) => String(customer.id) === String(form.customer_id)) || null,
    [meta.customers, form.customer_id]
  );

  const supplyType = useMemo(() => {
    if (!selectedCustomer || !meta.gst_config?.state_code) return "INTRA";
    return selectedCustomer.state_code && selectedCustomer.state_code !== meta.gst_config.state_code
      ? "INTER"
      : "INTRA";
  }, [selectedCustomer, meta.gst_config]);

  const isGstInvoice = form.invoice_type !== "NON_GST";

  const visibleCustomers = useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    if (!q) return meta.customers;
    return meta.customers.filter((customer) =>
      [customer.name, customer.code, customer.gstin].some((value) =>
        String(value || "").toLowerCase().includes(q)
      )
    );
  }, [meta.customers, customerQuery]);

  const calculatedItems = useMemo(() => {
    return form.items.map((item) => {
      const qty = numberValue(item.quantity || 0);
      const rate = numberValue(item.rate || 0);
      const amount = qty * rate;
      const taxInfo = findRate(hsnCodes, item.item_type, supplyType, isGstInvoice);
      const discountAmount = amount * (numberValue(form.discount_pct) / 100);
      const taxable = amount - discountAmount;
      const cgstAmount = taxable * (taxInfo.cgst / 100);
      const sgstAmount = taxable * (taxInfo.sgst / 100);
      const igstAmount = taxable * (taxInfo.igst / 100);
      return {
        ...item,
        hsn_code: taxInfo.hsn_code || item.hsn_code || "",
        amount,
        taxable_amount: taxable,
        cgst_rate: taxInfo.cgst,
        sgst_rate: taxInfo.sgst,
        igst_rate: taxInfo.igst,
        cgst_amount: cgstAmount,
        sgst_amount: sgstAmount,
        igst_amount: igstAmount,
        total_amount: taxable + cgstAmount + sgstAmount + igstAmount,
      };
    });
  }, [form.items, form.discount_pct, hsnCodes, supplyType, isGstInvoice]);

  const totals = useMemo(() => {
    const subtotal = calculatedItems.reduce((sum, item) => sum + item.amount, 0);
    const discountAmount = subtotal * (numberValue(form.discount_pct) / 100);
    const taxableAmount = subtotal - discountAmount;
    const cgstAmount = calculatedItems.reduce((sum, item) => sum + item.cgst_amount, 0);
    const sgstAmount = calculatedItems.reduce((sum, item) => sum + item.sgst_amount, 0);
    const igstAmount = calculatedItems.reduce((sum, item) => sum + item.igst_amount, 0);
    const makingCharges = calculatedItems
      .filter((item) => item.item_type === "MAKING_CHARGES")
      .reduce((sum, item) => sum + item.taxable_amount, 0);
    const stoneCharges = calculatedItems
      .filter((item) => item.item_type === "STONE")
      .reduce((sum, item) => sum + item.taxable_amount, 0);
    const totalTax = cgstAmount + sgstAmount + igstAmount;
    const totalAmount = taxableAmount + totalTax + numberValue(form.other_charges);
    const balanceDue = totalAmount - numberValue(form.advance_deducted);
    return {
      subtotal,
      discountAmount,
      taxableAmount,
      cgstAmount,
      sgstAmount,
      igstAmount,
      totalTax,
      makingCharges,
      stoneCharges,
      totalAmount,
      balanceDue,
    };
  }, [calculatedItems, form.discount_pct, form.other_charges, form.advance_deducted]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const setItemField = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const addItem = (itemType = "GOLD_JEWELLERY") => {
    setForm((prev) => ({ ...prev, items: [...prev.items, emptyItem(itemType)] }));
  };

  const removeItem = (index) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const changeInvoiceType = async (invoiceType) => {
    setForm((prev) => ({ ...prev, invoice_type: invoiceType }));
    await loadData(invoiceType);
  };

  const submitInvoice = async (afterCreate) => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        is_gst_invoice: isGstInvoice,
        customer_id: Number(form.customer_id),
        discount_pct: numberValue(form.discount_pct),
        advance_deducted: numberValue(form.advance_deducted),
        other_charges: numberValue(form.other_charges),
        items: calculatedItems.map((item) => ({
          description: item.description,
          item_type: item.item_type,
          hsn_code: item.hsn_code,
          quantity: numberValue(item.quantity),
          unit: item.unit,
          rate: numberValue(item.rate),
          weight_g: numberValue(item.weight_g),
          purity_pct: numberValue(item.purity_pct),
          design_code: item.design_code,
        })),
      };
      const res = await api.post("/accounts/invoices", payload);
      const createdId = res.data.id;
      if (afterCreate === "preview") {
        onCreated?.({ id: createdId, mode: "detail" });
      } else if (afterCreate === "submit") {
        await api.post(`/accounts/invoices/${createdId}/submit-approval`);
        onCreated?.({ id: createdId, mode: "detail" });
      } else {
        onCreated?.({ id: createdId, mode: "list" });
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create invoice.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="rounded-3xl border border-slate-200 bg-white p-8">Loading invoice form...</div>;
  }

  return (
    <div className="space-y-6">
      {error && <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-6 py-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-xl font-bold text-slate-900">Create Invoice</h3>
              <p className="text-sm text-slate-500">Invoice No: {meta.next_invoice_no || "Auto-generated on save"}</p>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={onCancel}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <X size={15} /> Close
              </button>
              {invoiceTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => changeInvoiceType(type.value)}
                  className={`rounded-xl px-3 py-2 text-sm font-semibold ${
                    form.invoice_type === type.value
                      ? "bg-indigo-600 text-white"
                      : "border border-slate-200 bg-white text-slate-700"
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid gap-4 p-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-5">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Field label="Invoice Date">
                <input type="date" value={form.invoice_date} onChange={(e) => setField("invoice_date", e.target.value)} className="input" />
              </Field>
              <Field label="Due Date">
                <input type="date" value={form.due_date} onChange={(e) => setField("due_date", e.target.value)} className="input" />
              </Field>
              <Field label="Supply Type">
                <div className={`banner ${supplyType === "INTER" ? "banner-warn" : "banner-info"}`}>{supplyType}</div>
              </Field>
              <Field label="Payment Terms">
                <input value={form.payment_terms} onChange={(e) => setField("payment_terms", e.target.value)} className="input" placeholder="NET 30" />
              </Field>
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <h4 className="font-semibold text-slate-900">Customer</h4>
                  <p className="text-sm text-slate-500">Search and select a customer to auto-detect GST and supply type.</p>
                </div>
              </div>
              <input
                value={customerQuery}
                onChange={(e) => setCustomerQuery(e.target.value)}
                className="input mb-3"
                placeholder="Search by name, code, or GSTIN"
              />
              <select value={form.customer_id} onChange={(e) => setField("customer_id", e.target.value)} className="input">
                <option value="">Select customer</option>
                {visibleCustomers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name} ({customer.code}) {customer.gstin ? `- ${customer.gstin}` : ""}
                  </option>
                ))}
              </select>
              {selectedCustomer && (
                <div className="mt-3 grid gap-3 rounded-2xl bg-slate-50 p-4 md:grid-cols-2">
                  <Info label="GSTIN" value={selectedCustomer.gstin || "Non-GST"} />
                  <Info label="Outstanding" value={`Rs. ${selectedCustomer.outstanding_balance.toLocaleString()}`} />
                  <Info label="State Code" value={selectedCustomer.state_code || "-"} />
                  <Info label="Address" value={selectedCustomer.full_address || "-"} />
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h4 className="font-semibold text-slate-900">Items</h4>
                  <p className="text-sm text-slate-500">Multiple items with HSN-wise tax breakup and auto calculation.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => addItem()} className="secondary-btn"><Plus size={14} /> Add Item</button>
                  <button onClick={() => addItem("MAKING_CHARGES")} className="secondary-btn"><Plus size={14} /> Add Making Charges</button>
                  <button onClick={() => addItem("STONE")} className="secondary-btn"><Plus size={14} /> Add Stone Charges</button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-[1280px] w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-3 min-w-[240px]">Description</th>
                      <th className="px-3 py-3 min-w-[180px]">Type</th>
                      <th className="px-3 py-3 min-w-[90px]">HSN</th>
                      <th className="px-3 py-3 min-w-[130px]">Qty</th>
                      <th className="px-3 py-3 min-w-[140px]">Unit</th>
                      <th className="px-3 py-3 min-w-[160px]">Rate</th>
                      <th className="px-3 py-3 min-w-[150px]">Weight(g)</th>
                      <th className="px-3 py-3 min-w-[130px]">Amount</th>
                      {isGstInvoice && supplyType === "INTER" && <th className="px-3 py-3">IGST</th>}
                      {isGstInvoice && supplyType !== "INTER" && (
                        <>
                          <th className="px-3 py-3">CGST</th>
                          <th className="px-3 py-3">SGST</th>
                        </>
                      )}
                      <th className="px-3 py-3">Item Total</th>
                      <th className="px-3 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {calculatedItems.map((item, index) => (
                      <tr key={index} className="border-t border-slate-100 align-top">
                        <td className="px-3 py-3"><input value={item.description} onChange={(e) => setItemField(index, "description", e.target.value)} className="input min-w-[240px]" /></td>
                        <td className="px-3 py-3">
                          <select value={item.item_type} onChange={(e) => setItemField(index, "item_type", e.target.value)} className="input min-w-[180px]">
                            {itemTypes.map((type) => <option key={type} value={type}>{type}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-3 text-xs font-semibold text-indigo-700">{item.hsn_code || "-"}</td>
                        <td className="px-3 py-3"><input type="number" value={item.quantity} onChange={(e) => setItemField(index, "quantity", e.target.value)} className="input min-w-[120px]" /></td>
                        <td className="px-3 py-3"><input value={item.unit} onChange={(e) => setItemField(index, "unit", e.target.value)} className="input min-w-[130px]" /></td>
                        <td className="px-3 py-3"><input type="number" value={item.rate} onChange={(e) => setItemField(index, "rate", e.target.value)} className="input min-w-[150px]" /></td>
                        <td className="px-3 py-3"><input type="number" value={item.weight_g} onChange={(e) => setItemField(index, "weight_g", e.target.value)} className="input min-w-[140px]" /></td>
                        <td className="px-3 py-3 text-sm font-semibold">Rs. {item.amount.toFixed(2)}</td>
                        {isGstInvoice && supplyType === "INTER" && <td className="px-3 py-3 text-xs">{item.igst_rate}% / Rs. {item.igst_amount.toFixed(2)}</td>}
                        {isGstInvoice && supplyType !== "INTER" && (
                          <>
                            <td className="px-3 py-3 text-xs">{item.cgst_rate}% / Rs. {item.cgst_amount.toFixed(2)}</td>
                            <td className="px-3 py-3 text-xs">{item.sgst_rate}% / Rs. {item.sgst_amount.toFixed(2)}</td>
                          </>
                        )}
                        <td className="px-3 py-3 font-semibold">Rs. {item.total_amount.toFixed(2)}</td>
                        <td className="px-3 py-3">
                          {form.items.length > 1 && (
                            <button onClick={() => removeItem(index)} className="table-btn text-rose-600">
                              <X size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <section className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <h4 className="mb-3 font-semibold text-slate-900">Totals</h4>
              <TotalRow label="Subtotal" value={totals.subtotal} />
              <TotalRow label="Discount" value={totals.discountAmount} />
              <TotalRow label="Taxable Amount" value={totals.taxableAmount} />
              {isGstInvoice && supplyType !== "INTER" && (
                <>
                  <TotalRow label="CGST" value={totals.cgstAmount} />
                  <TotalRow label="SGST" value={totals.sgstAmount} />
                </>
              )}
              {isGstInvoice && supplyType === "INTER" && <TotalRow label="IGST" value={totals.igstAmount} />}
              <TotalRow label="Making Charges" value={totals.makingCharges} />
              <TotalRow label="Stone Charges" value={totals.stoneCharges} />
              <Field label="Other Charges">
                <input type="number" value={form.other_charges} onChange={(e) => setField("other_charges", e.target.value)} className="input" />
              </Field>
              <Field label="Advance Deducted">
                <input type="number" value={form.advance_deducted} onChange={(e) => setField("advance_deducted", e.target.value)} className="input" />
              </Field>
              <div className="mt-4 rounded-2xl bg-slate-900 px-4 py-3 text-white">
                <div className="text-xs uppercase tracking-wide text-slate-300">Total</div>
                <div className="text-2xl font-bold">Rs. {totals.totalAmount.toFixed(2)}</div>
              </div>
              <div className="rounded-2xl bg-rose-50 px-4 py-3 text-rose-700">
                <div className="text-xs uppercase tracking-wide">Balance Due</div>
                <div className="text-xl font-bold">Rs. {totals.balanceDue.toFixed(2)}</div>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-4">
              <Field label="Notes">
                <textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} className="input min-h-[120px]" />
              </Field>
            </section>
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <button onClick={onCancel} className="secondary-btn">Cancel</button>
          <button disabled={saving} onClick={() => submitInvoice("draft")} className="primary-btn"><Save size={15} /> Save Draft</button>
          <button disabled={saving} onClick={() => submitInvoice("preview")} className="secondary-btn">Save & Preview</button>
          <button disabled={saving} onClick={() => submitInvoice("submit")} className="primary-btn"><Send size={15} /> Submit for Approval</button>
        </div>
      </section>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.75rem;
          border: 1px solid rgb(203 213 225);
          padding: 0.65rem 0.85rem;
          background: white;
        }
        .primary-btn, .secondary-btn, .table-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          border-radius: 0.75rem;
          padding: 0.65rem 1rem;
          font-size: 0.875rem;
          font-weight: 600;
        }
        .primary-btn { background: rgb(79 70 229); color: white; }
        .secondary-btn, .table-btn { border: 1px solid rgb(203 213 225); background: white; color: rgb(51 65 85); }
        .banner {
          display: flex;
          align-items: center;
          min-height: 44px;
          border-radius: 0.75rem;
          padding: 0.65rem 0.85rem;
          font-weight: 700;
        }
        .banner-info { background: rgb(224 231 255); color: rgb(67 56 202); }
        .banner-warn { background: rgb(254 243 199); color: rgb(180 83 9); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</label>
      {children}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-sm text-slate-800">{value}</div>
    </div>
  );
}

function TotalRow({ label, value }) {
  return (
    <div className="mb-2 flex items-center justify-between text-sm text-slate-700">
      <span>{label}</span>
      <span className="font-semibold">Rs. {value.toFixed(2)}</span>
    </div>
  );
}

export default InvoiceCreate;
